/**
 * Block-level LCS diff over TipTap block ids (attrs.id).
 * Used to materialize EditSuggestion rows when comparing versions.
 */

export type BlockRef = {
  id: string;
  /** Serialized TipTap node JSON (or plain text) for the block. */
  content: unknown;
  order: number;
};

export type BlockDiffKind = 'equal' | 'modified' | 'inserted' | 'deleted' | 'moved';

export type BlockDiffEntry = {
  kind: BlockDiffKind;
  blockId: string;
  /** Present for equal / modified / deleted / moved-from. */
  baseContent?: unknown;
  /** Present for equal / modified / inserted / moved-to. */
  proposedContent?: unknown;
  orderHint: number;
};

function contentKey(content: unknown): string {
  try {
    return JSON.stringify(content);
  } catch {
    return String(content);
  }
}

/**
 * LCS over block id sequences, then classify remaining as insert/delete/modify/move.
 * Modified = same id, different content. Moved = id appears in both but not aligned by LCS.
 */
export function diffBlocksById(base: BlockRef[], proposed: BlockRef[]): BlockDiffEntry[] {
  const n = base.length;
  const m = proposed.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      if (base[i].id === proposed[j].id) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const lcsPairs: Array<{ bi: number; pj: number }> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (base[i].id === proposed[j].id) {
      lcsPairs.push({ bi: i, pj: j });
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i += 1;
    } else {
      j += 1;
    }
  }

  const inLcsBase = new Set(lcsPairs.map((p) => p.bi));
  const inLcsProp = new Set(lcsPairs.map((p) => p.pj));
  const baseById = new Map(base.map((b) => [b.id, b]));
  const propById = new Map(proposed.map((b) => [b.id, b]));

  const out: BlockDiffEntry[] = [];
  let orderHint = 0;

  // Walk proposed order for equals/modified/inserts; emit deletes for base-only.
  const emittedBase = new Set<number>();
  const emittedProp = new Set<number>();

  for (const { bi, pj } of lcsPairs) {
    // Deletes before this LCS match
    for (let bi2 = 0; bi2 < bi; bi2 += 1) {
      if (emittedBase.has(bi2) || inLcsBase.has(bi2)) continue;
      const b = base[bi2];
      if (propById.has(b.id)) {
        // Will be handled as moved when we see proposed side, or skip if already in LCS elsewhere
        continue;
      }
      out.push({
        kind: 'deleted',
        blockId: b.id,
        baseContent: b.content,
        orderHint: orderHint++,
      });
      emittedBase.add(bi2);
    }
    for (let pj2 = 0; pj2 < pj; pj2 += 1) {
      if (emittedProp.has(pj2) || inLcsProp.has(pj2)) continue;
      const p = proposed[pj2];
      if (baseById.has(p.id) && !inLcsBase.has(base.findIndex((b) => b.id === p.id))) {
        out.push({
          kind: 'moved',
          blockId: p.id,
          baseContent: baseById.get(p.id)?.content,
          proposedContent: p.content,
          orderHint: orderHint++,
        });
      } else if (!baseById.has(p.id)) {
        out.push({
          kind: 'inserted',
          blockId: p.id,
          proposedContent: p.content,
          orderHint: orderHint++,
        });
      }
      emittedProp.add(pj2);
    }

    const b = base[bi];
    const p = proposed[pj];
    const same = contentKey(b.content) === contentKey(p.content);
    out.push({
      kind: same ? 'equal' : 'modified',
      blockId: b.id,
      baseContent: b.content,
      proposedContent: p.content,
      orderHint: orderHint++,
    });
    emittedBase.add(bi);
    emittedProp.add(pj);
  }

  for (let bi2 = 0; bi2 < n; bi2 += 1) {
    if (emittedBase.has(bi2)) continue;
    const b = base[bi2];
    if (propById.has(b.id)) continue;
    out.push({
      kind: 'deleted',
      blockId: b.id,
      baseContent: b.content,
      orderHint: orderHint++,
    });
  }
  for (let pj2 = 0; pj2 < m; pj2 += 1) {
    if (emittedProp.has(pj2)) continue;
    const p = proposed[pj2];
    if (baseById.has(p.id)) {
      out.push({
        kind: 'moved',
        blockId: p.id,
        baseContent: baseById.get(p.id)?.content,
        proposedContent: p.content,
        orderHint: orderHint++,
      });
    } else {
      out.push({
        kind: 'inserted',
        blockId: p.id,
        proposedContent: p.content,
        orderHint: orderHint++,
      });
    }
  }

  return out.filter((e) => e.kind !== 'equal');
}
